import { useState, useRef, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import { encodeWAV } from "./audio/encodeWAV";

// AudioWorklet processor code as string - Simplified for streaming
const AUDIO_WORKLET_CODE = `
class STTAudioProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buffer = [];
    this.bufferSize = 512;
    this.isShuttingDown = false;

    this.port.onmessage = (event) => {
      const { type } = event.data;
      if (type === 'shutdown') {
        this.isShuttingDown = true;
        this.flushBuffer();
      } else if (type === 'forceProcess') {
        this.flushBuffer();
      }
    };
  }

  flushBuffer() {
    if (this.buffer.length > 0) {
      const audioData = new Float32Array(this.buffer);
      this.port.postMessage({
        type: 'audioData',
        data: audioData,
        timestamp: Date.now(),
        isShuttingDown: this.isShuttingDown
      });
      this.buffer = [];
    }
  }

  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0] || this.isShuttingDown) return true;

    const inputChannel = input[0];

    for (let i = 0; i < inputChannel.length; i++) {
      this.buffer.push(inputChannel[i]);
    }

    if (this.buffer.length >= this.bufferSize) {
      this.flushBuffer();
    }

    return true;
  }
}

registerProcessor('stt-audio-processor', STTAudioProcessor);
`;

const SAMPLE_RATE = 16000;
const TRANSCRIPTION_TIMEOUT_MS = 15000;
// How much newly-captured audio to batch before sending a live partial
// transcript request. Lower = more responsive captions, more DeepInfra calls.
const PARTIAL_CHUNK_SAMPLES = SAMPLE_RATE * 2; // ~2s

/**
 * Modern STT hook using AudioWorklet for capture and the MinoLLM-Backend
 * socket.io STT service (same backend used by useVoiceChat) for transcription.
 */
export default function useSTT({
  onTranscriptionChange,
  onFinalTranscription,
  onError,
} = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [currentTranscription, setCurrentTranscription] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  // True from the moment recording stops until the authoritative final
  // transcript comes back (or fails) - lets the UI show a "cleaning up
  // transcription" state instead of just disappearing.
  const [isFinalizing, setIsFinalizing] = useState(false);
  // Kept for API compatibility with callers; this hook has no server-driven
  // auto-stop (the backend doesn't support it), so this never becomes true.
  const [isAutoStopping] = useState(false);
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);

  const audioChunksRef = useRef([]);
  const lastPartialOffsetRef = useRef(0);
  const livePartialTextRef = useRef("");
  const workletNodeRef = useRef(null);
  const sourceRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const isStoppingRef = useRef(false);
  const workletBlobURLRef = useRef(null);
  const { user } = useSelector((state) => state.app.datastate);
  const socketRef = useRef(null);

  const cleanupResources = useCallback(() => {
    if (workletNodeRef.current) {
      try {
        workletNodeRef.current.port.postMessage({ type: "shutdown" });
        workletNodeRef.current.port.onmessage = null;
        workletNodeRef.current.disconnect();
      } catch (e) {}
      workletNodeRef.current = null;
    }

    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch (e) {}
      sourceRef.current = null;
    }

    if (audioContextRef.current) {
      try {
        audioContextRef.current.close().catch(() => {});
      } catch (e) {}
      audioContextRef.current = null;
    }

    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => track.stop());
      } catch (e) {}
      streamRef.current = null;
    }

    if (workletBlobURLRef.current) {
      URL.revokeObjectURL(workletBlobURLRef.current);
      workletBlobURLRef.current = null;
    }

    if (socketRef.current) {
      try {
        socketRef.current.disconnect();
      } catch (e) {}
      socketRef.current = null;
    }

    audioChunksRef.current = [];
  }, []);

  const emergencyStopRecording = useCallback(() => {
    isStoppingRef.current = true;

    setIsRecording(false);
    setIsProcessing(false);
    setIsFinalizing(false);
    setIsInitializing(false);
    cleanupResources();
  }, [cleanupResources]);

  const connectSocket = useCallback(() => {
    return new Promise((resolve, reject) => {
      const socket = io(import.meta.env.VITE_SOCKET_URL, {
        query: { user: user._id, userToken: `Bearer ${user.userToken}` },
        transports: ["websocket", "polling"],
        timeout: 20000,
        forceNew: true,
      });

      socketRef.current = socket;

      // Live partial captions: each chunk is transcribed independently by
      // the backend, so we append rather than overwrite.
      socket.on("TRANSCRIPT_PARTIAL", (data) => {
        const text = data?.text?.trim();
        if (!text) return;

        livePartialTextRef.current = (livePartialTextRef.current + " " + text).trim();
        setCurrentTranscription(livePartialTextRef.current);
        onTranscriptionChange?.(livePartialTextRef.current);
      });

      const handleConnectError = (err) => {
        socket.off("connect", handleConnect);
        reject(new Error(`STT socket connection failed: ${err.message}`));
      };

      const handleConnect = () => {
        socket.off("connect_error", handleConnectError);
        resolve(socket);
      };

      socket.once("connect", handleConnect);
      socket.once("connect_error", handleConnectError);
    });
  }, [user, onTranscriptionChange]);

  // Send any newly-captured audio (since the last partial send) as a
  // standalone chunk for a live, non-authoritative partial transcript.
  const maybeSendPartialChunk = useCallback((force = false) => {
    const totalLength = audioChunksRef.current.reduce((acc, c) => acc + c.length, 0);
    const newSamples = totalLength - lastPartialOffsetRef.current;

    if (!socketRef.current?.connected) return;
    if (!force && newSamples < PARTIAL_CHUNK_SAMPLES) return;
    if (newSamples <= 0) return;

    const combined = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of audioChunksRef.current) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }

    const segment = combined.subarray(lastPartialOffsetRef.current, totalLength);
    lastPartialOffsetRef.current = totalLength;

    const wavBlob = encodeWAV(segment, SAMPLE_RATE);
    wavBlob.arrayBuffer().then((arrayBuffer) => {
      if (!socketRef.current?.connected) return;
      socketRef.current.emit("audio-chunk", {
        chunk: Array.from(new Uint8Array(arrayBuffer)),
        language: "en",
        userId: user?._id,
        timestamp: Date.now(),
      });
    });
  }, [user?._id]);

  const startRecording = useCallback(async () => {
    if (isRecording || isInitializing) {
      return;
    }

    try {
      setIsInitializing(true);
      setIsProcessing(true);
      isStoppingRef.current = false;
      setError(null);
      audioChunksRef.current = [];
      lastPartialOffsetRef.current = 0;
      livePartialTextRef.current = "";
      setCurrentTranscription("");

      if (typeof window === "undefined" || typeof navigator === "undefined") {
        throw new Error("Voice recording is not available in this environment");
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Voice recording is not supported in this browser.");
      }

      if (
        window.location.protocol !== "https:" &&
        window.location.hostname !== "localhost" &&
        window.location.hostname !== "127.0.0.1"
      ) {
        throw new Error("Voice recording requires HTTPS or localhost.");
      }

      await connectSocket();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: SAMPLE_RATE,
          channelCount: 1,
        },
      });

      streamRef.current = stream;

      audioContextRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });

      const workletBlob = new Blob([AUDIO_WORKLET_CODE], {
        type: "application/javascript",
      });

      workletBlobURLRef.current = URL.createObjectURL(workletBlob);

      await audioContextRef.current.audioWorklet.addModule(
        workletBlobURLRef.current
      );

      workletNodeRef.current = new AudioWorkletNode(
        audioContextRef.current,
        "stt-audio-processor"
      );

      workletNodeRef.current.port.onmessage = (event) => {
        const { type, data } = event.data;

        if (type === "audioData") {
          audioChunksRef.current.push(data);
          maybeSendPartialChunk();
        }
      };

      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(workletNodeRef.current);

      setIsRecording(true);
      setIsInitializing(false);
      setIsProcessing(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Recording failed";
      setError(msg);
      onError?.(msg);
      cleanupResources();
      setIsInitializing(false);
      setIsProcessing(false);
    }
  }, [isRecording, isInitializing, onError, cleanupResources, connectSocket, maybeSendPartialChunk]);

  const stopRecording = useCallback(async () => {
    if (!isRecording || isStoppingRef.current) {
      return currentTranscription;
    }

    isStoppingRef.current = true;
    setIsRecording(false);
    setIsProcessing(true);
    setIsFinalizing(true);

    try {
      workletNodeRef.current?.port.postMessage({ type: "forceProcess" });

      const totalLength = audioChunksRef.current.reduce(
        (acc, chunk) => acc + chunk.length,
        0
      );

      if (totalLength === 0 || !socketRef.current?.connected) {
        emergencyStopRecording();
        return "";
      }

      const combinedAudio = new Float32Array(totalLength);
      let offset = 0;
      for (const chunk of audioChunksRef.current) {
        combinedAudio.set(chunk, offset);
        offset += chunk.length;
      }

      const wavBlob = encodeWAV(combinedAudio, SAMPLE_RATE);
      const arrayBuffer = await wavBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const finalTranscript = await new Promise((resolve, reject) => {
        const socket = socketRef.current;

        const cleanup = () => {
          clearTimeout(timeoutId);
          socket.off("TRANSCRIPT_FINAL", handleFinal);
          socket.off("TRANSCRIPT_ERROR", handleTranscriptError);
        };

        const handleFinal = (data) => {
          cleanup();
          resolve(data?.text?.trim() || "");
        };

        const handleTranscriptError = (data) => {
          cleanup();
          reject(new Error(data?.message || "Transcription failed"));
        };

        const timeoutId = setTimeout(() => {
          cleanup();
          reject(new Error("STT timeout"));
        }, TRANSCRIPTION_TIMEOUT_MS);

        socket.on("TRANSCRIPT_FINAL", handleFinal);
        socket.on("TRANSCRIPT_ERROR", handleTranscriptError);

        socket.emit("audio-end", {
          userId: user?._id,
          combinedAudio: uint8Array,
        });
      });

      if (finalTranscript) {
        setCurrentTranscription(finalTranscript);
        onTranscriptionChange?.(finalTranscript);
        onFinalTranscription?.(finalTranscript);
      }

      cleanupResources();
      setIsProcessing(false);
      setIsFinalizing(false);

      return finalTranscript;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to stop recording";
      setError(msg);
      onError?.(msg);
      emergencyStopRecording();
      return "";
    } finally {
      isStoppingRef.current = false;
    }
  }, [
    isRecording,
    currentTranscription,
    user?._id,
    onTranscriptionChange,
    onFinalTranscription,
    onError,
    cleanupResources,
    emergencyStopRecording,
  ]);

  useEffect(() => {
    return () => {
      emergencyStopRecording();
    };
  }, [emergencyStopRecording]);

  return {
    isRecording,
    currentTranscription,
    isProcessing,
    isFinalizing,
    isAutoStopping,
    startRecording,
    stopRecording,
    emergencyStopRecording,
    error,
    mediaStream: streamRef.current, // Expose mediaStream for waveform visualization
  };
}
